import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Grid, Box, Button, Text, Link
} from 'components/ui';
import TextInput from 'components/Inputs/TextInput/TextInput';
import InputEmail from 'components/Inputs/InputEmail/InputEmail';
import FormValidator from 'utils/FormValidator';
import ErrorConstants from 'utils/ErrorConstants';
import ErrorsUtils from 'utils/Errors';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import { wrapFunctionalComponent } from 'utils/framework';
import Debounce from 'utils/Debounce';
import checkoutApi from 'services/api/checkout';
import { refreshCheckout } from 'services/Checkout';
import decorators from 'utils/decorators';
import { INTERSTICE_DELAY_MS } from 'constants/Checkout';
import UI from 'utils/UI';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import { FC_CHECKOUT_PATH, FC_CHECKOUT_SECTIONS, SECTION_NAMES } from 'constants/frictionlessCheckout';
import OrderActions from 'actions/OrderActions';
import historyLocationActions from 'actions/framework/HistoryLocationActions';
import checkoutUtils from 'utils/Checkout';
import pathSelector from 'selectors/historyLocation/pathSelector';
import PickupPersonDetailsEN from 'components/FrictionlessCheckout/BopisSection/PickupInfoCard/locales/PickupPersonDetails_en_US';
import PickupPersonDetailsFR from 'components/FrictionlessCheckout/BopisSection/PickupInfoCard/locales/PickupPersonDetails_fr_CA';

const { getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(PickupPersonDetailsEN, PickupPersonDetailsFR);

const pickupPersonValidators = {
    handleValidateFirstName: firstName => {
        if (FormValidator.isEmpty(firstName)) {
            return ErrorConstants.ERROR_CODES.FIRST_NAME;
        }

        return null;
    },

    handleValidateLastName: lastName => {
        if (FormValidator.isEmpty(lastName)) {
            return ErrorConstants.ERROR_CODES.LAST_NAME;
        }

        return null;
    }
};

function PickupPersonDetails({
    firstName, lastName, email, title, isGuestOrder, isEditMode
}) {
    const firstNameInput = useRef(null);
    const lastNameInput = useRef(null);
    const emailInput = useRef(null);

    const dispatch = useDispatch();
    const currentPath = useSelector(pathSelector);

    const [formData, setFormData] = useState({
        firstName: firstName || '',
        lastName: lastName || '',
        email: email || ''
    });

    const handleFieldChange = event => {
        const { name, value } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        ErrorsUtils.clearErrors();
        const fieldsForValidation = [firstNameInput.current, lastNameInput.current, emailInput.current];

        ErrorsUtils.collectClientFieldErrors(fieldsForValidation);

        return !ErrorsUtils.validate(fieldsForValidation);
    };

    useEffect(() => {
        const validatePickupPersonForm = e => {
            e?.preventDefault();

            const isFormValid = validateForm();

            // When true: Form is valid but user is in edit mode → pickupInfoUnsavedError
            // When false: Form is incomplete/invalid → pickupInfoIncompleteError
            const errorMessage = isFormValid ? getText('pickupInfoUnsavedError') : getText('pickupInfoIncompleteError');

            dispatch(
                OrderActions.setCheckoutSectionErrors({
                    [SECTION_NAMES.BOPIS_PICKUP_INFO]: errorMessage
                })
            );

            UI.scrollTo({ elementId: SECTION_NAMES.BOPIS_PICKUP_INFO, hasOffset: false });

            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject();
        };

        if (isGuestOrder && isEditMode) {
            dispatch(OrderActions.setPlaceOrderPreHook(validatePickupPersonForm));
        }

        return () => {
            if (isGuestOrder && isEditMode) {
                dispatch(OrderActions.setPlaceOrderPreHook(null));
            }
        };
    }, [isGuestOrder, isEditMode]);

    const handleChangeClick = () => {
        const pickupPath = `${FC_CHECKOUT_PATH}/${FC_CHECKOUT_SECTIONS.PICKUP_INFO.path}`;

        if (currentPath !== pickupPath) {
            dispatch(historyLocationActions.replaceLocation({ path: pickupPath }));
        }
    };

    const handleSaveContinue = async () => {
        const isFormValid = validateForm();

        if (isFormValid) {
            // init Paze after user captures e-mail on BOPIS Guest Checkout
            checkoutUtils.initPazeForGuestCheckout(formData.email, isGuestOrder);

            try {
                await decorators.withInterstice(
                    checkoutApi.addPickupDetails,
                    INTERSTICE_DELAY_MS
                )({
                    email: formData.email,
                    firstName: formData.firstName,
                    lastName: formData.lastName
                });

                await refreshCheckout()();
                FrictionlessUtils.updateCheckoutURLBasedOnOrderCompleteness();
            } catch (error) {
                ErrorsUtils.collectAndValidateBackEndErrors(error);
            }
        }
    };

    if (!isEditMode) {
        const name = `${firstName} ${lastName}`;

        return (
            <div key='view-mode'>
                <Grid columns='1fr auto'>
                    <Text
                        is='h4'
                        fontWeight='bold'
                        lineHeight='tight'
                        children={title}
                    />
                    {isGuestOrder && (
                        <Link
                            color='link'
                            children={getText('change')}
                            onClick={handleChangeClick}
                        />
                    )}
                </Grid>
                <Box marginTop={2}>
                    <Text
                        is='p'
                        lineHeight='tight'
                        children={name}
                    />
                    <Text
                        is='p'
                        lineHeight='tight'
                        children={email}
                    />
                </Box>
            </div>
        );
    }

    return (
        <Box
            key='edit-mode'
            marginTop={2}
            width={['100%', null, '50%']}
        >
            <Grid
                columns={2}
                gap={3}
            >
                <TextInput
                    name='firstName'
                    marginBottom={null}
                    required={true}
                    label={getText('firstName')}
                    value={formData.firstName}
                    onChange={handleFieldChange}
                    maxLength={FormValidator.FIELD_LENGTHS.name}
                    ref={firstNameInput}
                    validateError={pickupPersonValidators.handleValidateFirstName}
                />
                <TextInput
                    name='lastName'
                    marginBottom={null}
                    required={true}
                    label={getText('lastName')}
                    value={formData.lastName}
                    onChange={handleFieldChange}
                    maxLength={FormValidator.FIELD_LENGTHS.name}
                    ref={lastNameInput}
                    validateError={pickupPersonValidators.handleValidateLastName}
                />
            </Grid>
            <Box marginTop={3}>
                <InputEmail
                    marginBottom={null}
                    required={true}
                    label={getText('email')}
                    name='email'
                    onChange={handleFieldChange}
                    login={formData.email}
                    ref={emailInput}
                />
            </Box>
            <Box marginTop={5}>
                <Button
                    variant='primary'
                    minWidth={[172, null, 200]}
                    onClick={Debounce.preventDoubleClick(handleSaveContinue)}
                >
                    {getText('saveContinue')}
                </Button>
            </Box>
        </Box>
    );
}

export default wrapFunctionalComponent(PickupPersonDetails, 'PickupPersonDetails');
